import React from 'react';

import { LoaderLine } from '@/components/LoaderLine';
import { PaymentSystemButton } from '@/components/PaymentSystemButton';
import { StripeCardForm } from '@/components/StripeCardForm';
import { PAYMENT_SECTION_ID } from '@/constants';
import { ONE_OFF_PLAN_IDS } from '@/constants/plans';
import { useUserStore } from '@/hooks/useAccount';
import { useDevmodeStripeV2Plans } from '@/hooks/useDevmodeStripeV2Plans';
import { useStripeV2Payment } from '@/hooks/useStripeV2Payment';
import { CombinedPaymentDescription } from '@/modals/CoinsPaywall/components/CombinedPaymentDescription';
import { StripeElements } from '@/pages/Paywall/components/StripeElements';
import { OneOffPaymentDescription } from '@/pages/Paywall/sections/PaymentMethodSelection/components/OneOffPaymentDescription/OneOffPaymentDescription';
import {
  Option,
  Options,
} from '@/pages/Paywall/sections/PaymentMethodSelection/components/Options';
import { PaymentDescription } from '@/pages/Paywall/sections/PaymentMethodSelection/components/PaymentDescription';
import {
  ApplePayLabel,
  CreditCardLabel,
} from '@/pages/Paywall/sections/PaymentMethodSelection/components/PaySysLabel';
import {
  SectionStyles,
  TitleStyles,
} from '@/pages/Paywall/sections/PaymentMethodSelection/PaymentMethodSelection.styles';
import { StripeV2PaymentRequest } from '@/services/stripe';
import { canUseApplePay } from '@/utils/canUseApplePay';

export const DevmodePaymentMethodSelection: React.FC<{
  theme?: 'domination' | 'dark-forest';
}> = ({ theme }) => {
  const user = useUserStore((state) => state.user);
  const devmodeClientSecret =
    user?.onboarding_data?.devmode_client_secret?.trim() || null;

  const { stripePlans, selectedPlan, selectedCoinPlan, billingPortalLink } =
    useDevmodeStripeV2Plans();

  // Determine if this is a one-off payment
  const isOneOffPlan = selectedPlan?.id
    ? selectedPlan.id.startsWith('pro.one_off_') ||
      ONE_OFF_PLAN_IDS.includes(
        selectedPlan.id as (typeof ONE_OFF_PLAN_IDS)[number],
      )
    : false;

  const [paymentTabs, setPaymentTabs] = React.useState<
    ('applePay' | 'creditCard' | 'googlePay')[]
  >(['creditCard']);

  const [paymentMethod, setPaymentMethod] = React.useState<
    'applePay' | 'creditCard' | 'googlePay'
  >(() => {
    return 'creditCard';
  });

  React.useEffect(() => {
    if (canUseApplePay()) {
      setPaymentTabs(['applePay', 'creditCard']);
      setPaymentMethod('applePay');
    }
  }, []);

  const {
    mutateAsync: createPayment,
    isLoading: isCreatingStripeSubscription,
  } = useStripeV2Payment();

  const [clientSecret, setClientSecret] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (devmodeClientSecret) {
      setClientSecret(devmodeClientSecret);
      return;
    }
    setClientSecret(null);
    const fetchClientSecret = async () => {
      if (!selectedPlan?.id) return;
      const paymentData: StripeV2PaymentRequest = {
        subscription_price_id: selectedPlan.id,
        ...(selectedCoinPlan && {
          coins_price_id: selectedCoinPlan.id,
        }),
      };
      const result = await createPayment(paymentData);
      setClientSecret(result.secret);
    };
    fetchClientSecret();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan, selectedCoinPlan, devmodeClientSecret]);

  const renderDescription = () => {
    if (!selectedPlan) return null;

    if (selectedCoinPlan) {
      return (
        <CombinedPaymentDescription
          selectedPlan={selectedPlan}
          selectedCoinPlan={selectedCoinPlan}
        />
      );
    }

    if (isOneOffPlan) {
      return <OneOffPaymentDescription selectedPlan={selectedPlan} />;
    }
    return <PaymentDescription selectedPlan={selectedPlan} />;
  };

  return (
    <SectionStyles id={PAYMENT_SECTION_ID}>
      <TitleStyles>Payment method</TitleStyles>

      {isCreatingStripeSubscription || !clientSecret || !stripePlans ? (
        <LoaderLine label="Loading payment elements..." />
      ) : (
        <StripeElements
          clientSecret={clientSecret}
          key={clientSecret}
          stripeTheme="flat"
        >
          {selectedPlan && (
            <Options>
              {paymentTabs.includes('applePay') && (
                <Option
                  selected={paymentMethod === 'applePay'}
                  onClick={() => setPaymentMethod('applePay')}
                  label="Apple Pay"
                  labelIcon={<ApplePayLabel />}
                >
                  {renderDescription()}
                  <PaymentSystemButton
                    plan={selectedPlan}
                    disabled={isCreatingStripeSubscription}
                    billing_portal_link={billingPortalLink || ''}
                    clientSecret={clientSecret}
                    selectedCoinPlan={selectedCoinPlan}
                  />
                </Option>
              )}
              <Option
                selected={paymentMethod === 'creditCard'}
                onClick={() => setPaymentMethod('creditCard')}
                label="Credit card"
                labelIcon={<CreditCardLabel />}
                theme={theme}
              >
                {renderDescription()}
                <StripeCardForm
                  clientSecret={clientSecret}
                  billing_portal_link={billingPortalLink || ''}
                  plan={selectedPlan}
                  disabled={isCreatingStripeSubscription}
                  theme={theme}
                  buttonText={isOneOffPlan ? 'Buy Now' : 'Pay'}
                  selectedCoinPlan={selectedCoinPlan}
                />
              </Option>
            </Options>
          )}
        </StripeElements>
      )}
    </SectionStyles>
  );
};
